import to from 'await-to-js';
import axios from 'axios';
import {
  cloneDeep,
  filter,
  forEach,
  get,
  groupBy,
  isNaN,
  isNil,
  keyBy,
  keys,
  map,
  mapValues,
  merge,
  omit,
  pick,
  size,
  some,
  sortBy,
  floor,
  min,
  max,
} from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
// eslint-disable-next-line
import { useDayjs } from '@/composables/use-dayjs';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useFeature } from '@/composables/use-feature';
import { jobApi } from '@/helpers';
import i18n from '@/plugins/vue-i18n';
import { useEventsStore } from '@/stores//events';
import { useAlertsStore } from '@/stores/alerts';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useAssortmentGroupsStore } from '@/stores/assortment-groups';
import { useBundlesStore } from '@/stores/bundles';
import { useSnackbarStore } from '@/stores/snackbar';
import jobapi from '@enums/jobapi';
import SIZE_TYPES from '@enums/size-types';
import SORT_DIRECTION from '@enums/sort-direction';
import WORK_PACKAGE_BUSINESS_UNIT from '@enums/workpackage-business-unit';
import WORK_PACKAGE_TYPE from '@enums/workpackage-type';
import baseStatus from '@sharedModules/data/baseStatus';
import { useContextStore } from './context';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useScenariosStore } from './scenarios';
import handleErrorMessages from './utils/validation';

const workpackageDefaultFields = [
  'name',
  'type',
  'description',
  'targetLaunchDate',
  'productCount',
  'storeCount',
  'jobs',
  'lastModifiedDate',
  'creationDate',
  'createdBy',
  'templateId',
  'bundleId',
  'selectedChain',
];

// Note: This format is client-agnostic - this is the DB format and is never customised.
const saveDateFormat = 'YYYY-MM-DDT00:00:00.000[Z]';
const dateFormat = 'YYYY-MM-DD';

export const useWorkpackagesStore = defineStore('workpackages', () => {
  const contextStore = useContextStore();
  const assortmentCanvasStore = useAssortmentCanvasStore();
  const bundlesStore = useBundlesStore();
  const eventsStore = useEventsStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();
  const scenariosStore = useScenariosStore();
  const assortmentGroupsStore = useAssortmentGroupsStore();

  // state
  const selectedWorkpackage = ref(null);
  const selectedWpLinkedWp = ref(null);
  const selectedWpLinkedScenario = ref(null);
  const savedWorkpackage = ref(null);
  const expanded = ref(false);
  const workpackages = ref([]);
  const archivedWorkpackages = ref([]);
  const additionalDownloadedWorkpackages = ref([]); // if we need to download wp without showing it on UI in any list
  const workpackageTemplates = ref([]);
  const archivedWorkpackageTemplates = ref([]);
  const archivedWorkpackagesTotalCount = ref(0);
  const archivedWorkpackageTemplatesTotalCount = ref(0);
  const archivedWorkpackagesCurrentPage = ref(1);
  const archivedWorkpackageTemplatesCurrentPage = ref(1);
  const isScenarioPanelExpanded = ref(false);
  const isWPSetupExpanded = ref(false);
  const loading = ref(false);
  const loadingArchivedWorkpackages = ref(false);
  const loadingWorkpackageTemplates = ref(false);
  const wpValidation = ref(false);
  const productsHierarchy = ref(null);
  const hierarchyDepth = ref(0);
  const isImportingFromPlano = ref(false);
  const lastSuccessfulImportFromPlanoTimestamp = ref(null);
  const proposedAssortmentGroups = ref([]);
  const loadingAGsForPeriodId = ref(false);
  const isApplyingPreSet = ref(false);

  // Test consolidating setup logic
  const wpDateSelections = ref({});
  const wpModelSelections = ref({});
  const wpSelectedAssortmentGroups = ref([]);
  const endDatesExceeded = ref({
    performance: false,
    switching: false,
    learning: false,
  });

  // Snapshot dates
  const latestSnapshotDate = shallowRef(null);
  const snapshotDates = ref([]);

  /**
   * Previous Vuex mutations are still in use
   */
  function addWorkpackageMutation(workpackage) {
    if (workpackage.isTemplate) {
      workpackageTemplates.value.push(workpackage);
    }
    workpackages.value.push(workpackage);
  }

  function updateWorkpackageMutation({ field, workpackageId, updates }) {
    const workpackageToUpdate = workpackages.value.find(wp => wp._id === workpackageId);

    if (workpackageToUpdate) workpackageToUpdate[field] = updates;
    else {
      const templateToUpdate = workpackageTemplates.value.find(wp => wp._id === workpackageId);
      if (templateToUpdate) templateToUpdate[field] = updates;
    }
  }

  function setArchivedWorkpackagesMutation(data) {
    const { workpackages: newWorkpackages, totalCount } = data;
    archivedWorkpackages.value = newWorkpackages;
    archivedWorkpackagesTotalCount.value = totalCount;
  }

  function updateNoteCountMutation({ workpackageId, difference = 1 }) {
    const workpackageToUpdate = workpackages.value.find(wp => wp._id === workpackageId);

    if (workpackageToUpdate) {
      workpackageToUpdate.totalNotes += difference;
    }
  }
  /**
   * Getters
   *  */
  const activeWorkpackages = computed(() => workpackages.value);
  const workpackagesById = computed(() => keyBy(workpackages.value, '_id'));
  const stores = computed(() => get(selectedWorkpackage.value?.stores, []));
  const useEndDateSelection = computed(() => useFeature().setByEndDateDefault.value);

  const formattedSnapshotDates = computed(() => {
    const { getTzZeroedDate } = useDayjs();
    return snapshotDates.value.map(getTzZeroedDate);
  });

  const isFullResetWP = computed(() =>
    WORK_PACKAGE_TYPE.STANDARD.includes(selectedWorkpackage.value.type)
  );
  const isSimpleSwapsWP = computed(() => {
    // Check if the store is ready
    if (!selectedWorkpackage.value) return false;

    return selectedWorkpackage.value.type === WORK_PACKAGE_TYPE.ASSORTMENT_SIMPLE_SWAPS || false;
  });
  const isFreshWP = computed(() => {
    // Check if the store is ready
    if (!selectedWorkpackage.value) return false;

    return selectedWorkpackage.value.type === WORK_PACKAGE_TYPE.ASSORTMENT_FRESH || false;
  });
  const isCopyWorkpackageRunning = computed(() =>
    some(
      workpackages.value.map(wp =>
        jobapi.jobInProgressStatuses.includes(get(wp, 'jobs.copyWorkpackage.status'))
      )
    )
  );
  const getSelectedWorkpackageExcludedDates = computed(() => forecastStartDate => {
    const { dayjs } = useDayjs();
    const dbExcludedDates = selectedWorkpackage.value.performanceExcludedDates;
    const weekStartDates = dbExcludedDates.filter(d => {
      return dayjs(d).weekday() === 1 && dayjs(d) > dayjs(forecastStartDate);
    });
    return weekStartDates;
  });

  const getUnitOfMeasure = computed(() => workpackage => {
    const sizeTypesSuffixes = {
      [SIZE_TYPES.linearSpace]: `${contextStore.getUnitOfMeasure}`,
      [SIZE_TYPES.horizontalSpace]: `${contextStore.getUnitOfMeasure}^2`,
      [SIZE_TYPES.frontalSpace]: `${contextStore.getUnitOfMeasure}^2`,
      [SIZE_TYPES.cubicSpace]: `${contextStore.getUnitOfMeasure}^3`,
      [SIZE_TYPES.productCount]: '#',
    };
    const fillInSelection = get(workpackage, 'fillInSelection');
    return get(sizeTypesSuffixes, fillInSelection, '');
  });

  // Get the section status for the workpackage
  const workpackageStatus = computed(() => workpackage => {
    // Overwrite the actual status over the base
    const actualStatus = merge({}, baseStatus.workpackageBaseStatusObject, workpackage.status);

    // Calculate if general sections are available
    actualStatus.scope.available = true;
    actualStatus.inputs.available = true;

    // Calculate if general sub sections are available for editing
    actualStatus.scope.datesAssortmentGroups.available = true;
    actualStatus.scope.products.available = true;
    actualStatus.scope.stores.available = true;
    actualStatus.scope.additionalProducts.available = true;

    actualStatus.inputs.attributeEditor.available = true;
    actualStatus.inputs.cannGroups.available = true;

    return actualStatus;
  });

  // Get the section status for the selected workpackage
  const selectedWorkpackageStatus = computed(() =>
    selectedWorkpackage.value ? workpackageStatus.value(selectedWorkpackage.value) : {}
  );
  const isWorkpackageSetupRunning = computed(() => {
    const selectedWorkpackageJosbStatus = get(
      selectedWorkpackage.value,
      'jobs.workpackageSetup.status'
    );
    return jobapi.jobInProgressStatuses.includes(selectedWorkpackageJosbStatus);
  });

  const templatesById = computed(() => keyBy(workpackageTemplates.value, '_id'));
  const workpackagesByBundle = computed(() => {
    const groupedWorkpackages = groupBy(workpackages.value, 'bundleId');
    // Allows workpackages that are not assigned to a bundle to be retrieved
    const noBundle = { _id: null };
    const bundles = [noBundle, ...bundlesStore.bundles];

    return bundles.reduce(
      (acc, { _id }) => ({
        ...acc,
        [_id]: groupedWorkpackages[_id || undefined],
      }),
      {}
    );
  });

  const getWorkpackageById = computed(() => id => {
    return workpackages.value.find(el => el._id === id);
  });

  /**
   * Actions
   *  */

  function setWorkpackageById(workpackage) {
    const indexOfWorkpackage = workpackages.value.findIndex(wp => wp._id === workpackage._id);
    if (indexOfWorkpackage === -1) {
      workpackages.value.push(workpackage);
    } else {
      workpackages.value[indexOfWorkpackage] = workpackage;
    }
  }

  function tddWorkpackageHelper(workpackage) {
    if (workpackage.isTemplate) {
      workpackageTemplates.value.push(workpackage);
    }
    workpackages.value.push(workpackage);
  }

  /* Fetches a single workpackage by ID, optionally including stores and assortment groups.
  When `isStatelessFetch` is true, it returns the workpackage without setting it in the store.
  Otherwise, it sets the workpackage in the store.
  */
  async function fetchSingleWorkpackage(options = {}) {
    const { includeStores, includeAssortmentGroups, workpackageId } = options;
    const pickedFields = [...workpackageDefaultFields];
    if (includeStores) {
      pickedFields.push('stores');
    }
    if (includeAssortmentGroups) {
      pickedFields.push('selectedAssortmentGroupSettings');
    }
    const params = {
      sortField: 'creationDate',
      sortDirection: SORT_DIRECTION.ascending,
      pick: pickedFields,
    };
    const [err, result] = await to(axios.get(`/api/workpackages/${workpackageId}`, { params }));
    if (err) throw new Error(err.message);
    const workpackage = result.data;
    if (options.isStatelessFetch) return workpackage;
    setWorkpackageById(workpackage);
  }

  /**
   * Fetches the linked workpackage for the given workpackage. This action does not set any state.
   * @param {Object} workpackage The workpackage whose linked workpackage should be fetched.
   * @returns {Object | null | undefined} The linked workpackage or null if the given workpackage has no linkedPreviousWorkpackageId
   * or undefined if linkedPreviousWorkpackageId is populated but the workpackage could not be found.
   */
  async function fetchLinkedWorkpackageForWorkpackage(workpackage) {
    if (!workpackage?.linkedPreviousWorkpackageId) {
      return null;
    }

    const linkedWorkpackage = await fetchSingleWorkpackage({
      workpackageId: workpackage.linkedPreviousWorkpackageId,
      // Ensure the `workpackages` state is not updated because the fetched workpackage could be archived
      // and archived workpackages should be stored in `archivedWorkpackages`.
      isStatelessFetch: true,
    });

    return linkedWorkpackage;
  }

  /**
   * Fetches the linked scenario for the given workpackage. This action does not set any state.
   * @param {Object} workpackage The workpackage whose linked scenario should be fetched.
   * @returns {Object | null | undefined} The linked scenario or null if the given workpackage has no linkedPreviousScenarioId
   * or undefined if linkedPreviousScenarioId is populated but the scenario could not be found.
   */
  async function fetchLinkedScenarioForWorkpackage(workpackage) {
    if (!workpackage?.linkedPreviousScenarioId) {
      return null;
    }

    const scenarios = await scenariosStore.fetchScenarios({
      params: { where: { _id: workpackage.linkedPreviousScenarioId } },
      // We're only pulling a single scenario here so don't commit the changes
      // because it will completely overwrite the `scenarios` state.
      options: { skipCommit: true },
    });

    // Only one scenario should ever be in the array.
    return scenarios?.[0];
  }

  /**
   * Action that sets the linked workpackage for the currently selected workpackage.
   * Not all selected workpackages will have a linked workpackage.
   * @param {Object} workpackage The workpackage whose linked workpackage should be fetched.
   */
  function setSelectedWpLinkedWp(linkedWorkpackage) {
    selectedWpLinkedWp.value = linkedWorkpackage;
  }

  /**
   * Action that sets the linked scenario for the currently selected workpackage.
   * Not all selected workpackages will have a linked scenario.
   */
  function setSelectedWpLinkedScenario(linkedScenario) {
    selectedWpLinkedScenario.value = linkedScenario;
  }

  /**
   * Fetches the linked workpackage and scenario for the given workpackage and sets the linked workpackage and scenario state.
   * @param {Object} workpackage The workpackage whose linked workpackage and scenario state should be refreshed.
   */
  async function setLinkedWorkpackageAndScenario(workpackage) {
    const [linkedWorkpackage, linkedScenario] = await Promise.all([
      fetchLinkedWorkpackageForWorkpackage(workpackage),
      fetchLinkedScenarioForWorkpackage(workpackage),
    ]);

    setSelectedWpLinkedWp(linkedWorkpackage);
    setSelectedWpLinkedScenario(linkedScenario);
  }

  /*
   * Action that sets the selected workpackage and opens a workpackage eventstream
   */
  function setSelectedWorkpackage(workpackage) {
    selectedWorkpackage.value = workpackage;
    savedWorkpackage.value = { ...workpackage };

    // Unset the scenario if it's not part of the workpackage.
    // This avoids cases where workpackages could be unselected, but an associated scenario persisted
    // If setting to the same workpackage, scenario is still kept if it's part of the wp
    const { selectedScenario } = scenariosStore;
    if (selectedScenario?._id && selectedScenario.workpackageId !== workpackage?._id) {
      scenariosStore.setSelectedScenario({});
    }

    if (workpackage) {
      // Open event stream for events of this workpackage
      eventsStore.resetEventStreamWithWorkpackageId({ workpackageId: workpackage._id });
    }

    setLinkedWorkpackageAndScenario(workpackage);
  }

  /**
   * Loads all of the state needed for a page based on the scenarioId and canvasId in it's params.
   * If loading state by workpackageId, the state will set based on the first scenario found for that
   * workpackage.
   */
  async function loadState({ scenarioId, canvasId, workpackageId }) {
    loading.value = true;
    if (workpackageId) {
      // If workpackageId exits, use it to fetch the scenarios
      const scenarioResult = await scenariosStore.fetchScenarios({
        params: { where: { workpackageId } },
      });
      // Set the scenarioId to the id of the first scenario returned
      // A template will only have one scenario
      scenarioId = get(scenarioResult[0], '_id');

      const [workpackageErr, workpackage] = await to(
        axios.get(`/api/workpackages/${workpackageId}`)
      );
      if (workpackageErr) throw new Error(workpackageErr.message);
      setSelectedWorkpackage(workpackage.data);
      loading.value = false;
    }

    if (!scenarioId) {
      // If there is no scenario to load, ensure scenario state is reset
      await scenariosStore.setSelectedScenario({});
      return;
    }

    const [err, result] = await to(
      axios.get(`/api/state/scenario/${scenarioId}`, { params: { canvasId } })
    );
    loading.value = false;
    if (err) throw new Error(err.message);

    if (!workpackageId) {
      // If workpackageId does not exits, use workpackageId from scenario
      // to fetch state for workpackage scenarios
      await scenariosStore.fetchScenarios({
        params: { where: { workpackageId: result.data.workpackage._id } },
      });
      setSelectedWorkpackage(result.data.workpackage);
    }

    await scenariosStore.setSelectedScenario(result.data.scenario);

    // fetch canvases after current scenario was set
    await assortmentCanvasStore.fetchCanvases({});

    if (result.data.canvas) {
      assortmentCanvasStore.setSelectedAssortmentCanvas(result.data.canvas);
    }

    // Open event stream for events for this sceanrio, after we update the current selected scenario
    eventsStore.resetEventStreamWithScenarioId({ scenarioId: result.data.scenario._id });
  }

  /**
   * Gets the previous chained workpackage for the given workpackage ID, if one is present.
   * The "previous chained workpackage" refers to a workpackage:
   *  - With a chain matching the provided chainId
   *  - That was created from the template linked to the provided workpackageId's template.
   * See the visualisation of this below (start at the 'Provided Workpackage' box):
   *
   *      |‾‾‾‾‾‾‾‾‾‾|  linkedPreviousWorkpackageId |‾‾‾‾‾‾‾‾‾‾|
   *      |   Prev.  |◀-----------------------------| Prov. WP |
   *      | Template |                              | Template |
   *      |__________|                              |__________|
   *           ▲                                         ▲
   *           | templateId                              | templateId
   *           |                                         |
   * |‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|                      |‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
   * |   Prev. Chained   |                      |      Provided     |
   * |    Workpackage    |                      |    Workpackage    |
   * | (chain === 'CFC') |                      | (chain === 'CFC') |
   * |___________________|                      |___________________|
   *
   * @param {Object} RORO The RORO wrapper.
   * @param {String} RORO.workpackageId The identifier of the workpackage to get the chained workpackage for.
   * @param {String} RORO.chainId The chain identifier that the chained workpackage belongs to (e.g. 'CFC').
   * @returns {Object | null} A workpackage object representing the previous chained workpackage. Or null when
   * no previous chained workpackage could be found.
   */
  async function getPreviousChainedWorkpackage({ workpackageId, chainId }) {
    const [err, result] = await to(
      axios.get(`/api/workpackages/${workpackageId}/previous-chained`, { params: { chainId } })
    );
    if (err || result.data?.errors) {
      throw new Error(i18n.t('errors.unableToGetPreviousChainedWorkpackage'));
    }
    return result?.data || null; // return as a null, rather than an empty string
  }

  async function getProductHierarchy(params = {}) {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/product-hierarchy', { params }));
    loading.value = false;
    if (err || (result.data && result.data.errors)) {
      throw new Error(i18n.t('errors.toolData.unableToGetProductsHierarchyTree'));
    }
    return result?.data;
  }

  async function getProductHierarchyDepth(params = {}) {
    const [err, result] = await to(axios.get('/api/tool-data/product-hierarchy/depth', { params }));
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetProductsHierarchyDepth'));
    }
    hierarchyDepth.value = result?.data.depth;
    return result?.data;
  }

  async function getProductsHierarchyProductsData({ productCategoryKeys }) {
    loading.value = true;
    const workpackageId = selectedWorkpackage.value._id;
    const [err, result] = await to(
      axios.post('/api/tool-data/products-hierarchy-data', { productCategoryKeys, workpackageId })
    );
    loading.value = false;
    if (err || (result.data && result.data.errors)) {
      throw new Error(
        i18n.t('errors.toolData.unableToGetProductsForHierarchyTree', { productCategoryKeys })
      );
    }
    return result?.data;
  }

  function getLatestDataDate() {
    const { dayjs } = useDayjs();
    const latestDataDate = dayjs.utc(latestSnapshotDate.value);

    return latestDataDate;
  }

  function getMaxDate(snapshotDate) {
    const { dayjs } = useDayjs();
    const latestDataDate = getLatestDataDate();
    snapshotDate = dayjs.utc(snapshotDate);
    return dayjs.min(snapshotDate, latestDataDate);
  }

  function changeEndDate(endDate, type, weeks) {
    const { dayjs } = useDayjs();
    // Weeks is only set for defaultDates
    weeks = weeks || wpDateSelections.value[`${type}DateWeeks`];
    const startDate = dayjs(endDate).subtract(weeks, 'weeks').add(1, 'day').format(dateFormat);

    return {
      [`${type}StartDate`]: startDate,
      [`${type}EndDate`]: endDate,
    };
  }

  function formatDates({ dates, format, parseFromDb = false }) {
    const { zeroOutTzOffset } = useDayjs();
    if (!format) format = dateFormat;

    const { dayjs } = useDayjs();
    const dateFieldsToFormat = pick(dates, [
      'snapshotDate',
      'targetLaunchDate',
      'performanceStartDate',
      'performanceEndDate',
      'switchingStartDate',
      'switchingEndDate',
      'learningStartDate',
      'learningEndDate',
    ]);

    const dateArraysToFormat = pick(dates, ['performanceExcludedDates', 'learningExcludedDates']);

    const dateFormatter = date => {
      if (isNil(date)) return date;

      if (parseFromDb) {
        return dayjs.utc(date).format(format);
      }
      return zeroOutTzOffset(date).startOf('day').format(format);
    };

    const stringDates = mapValues(dateFieldsToFormat, dateFormatter);
    const arrayDates = mapValues(dateArraysToFormat, dateArray => map(dateArray, dateFormatter));

    return {
      ...dates,
      ...stringDates,
      ...arrayDates,
    };
  }

  function getDefaultDates() {
    const { goToDay } = useDayjs();
    const { hasSeasonalityEnabled } = useFeature();

    const snapshotDate = latestSnapshotDate.value;

    const switchingDateWeeks = 104;
    const performanceDateWeeks = 52;

    const defaultInputs = {
      snapshotDate,
      switchingDateWeeks,
      performanceDateWeeks,
      targetLaunchDate: null,
      performanceExcludedDates: [],
      performancePeriodPreSet: null,
    };

    const maxDate = getMaxDate(snapshotDate);
    const lastAvailableSunday = goToDay(maxDate, 'prev', 'sunday');

    const startAndEndDates = {
      ...changeEndDate(lastAvailableSunday, 'switching', switchingDateWeeks),
      ...changeEndDate(lastAvailableSunday, 'performance', performanceDateWeeks),
    };

    const formattedDates = formatDates({
      dates: {
        ...defaultInputs,
        ...startAndEndDates,
      },
    });

    if (hasSeasonalityEnabled.value) {
      const learningDateWeeks = 52;
      const learningDates = changeEndDate(lastAvailableSunday, 'learning', learningDateWeeks);
      // Add seasonality dates when feature is enabled
      return {
        ...formattedDates,
        learningDateWeeks,
        learningExcludedDates: [],
        ...formatDates({ dates: learningDates }),
      };
    }

    return formattedDates;
  }

  function createWorkpackage(isTemplate, withoutBundle = false) {
    const { dayjs, getUTCNow } = useDayjs();
    // returns default workpackage data
    const utcStartOfDay = getUTCNow().startOf('day');
    const dateTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
    let wpName = i18n.t('entities.workpackage');
    if (isTemplate) wpName = `${i18n.t('entities.template')} ${wpName}`;

    const defaultDates = getDefaultDates();
    return {
      ...defaultDates,
      // WP name is after user offset from utc
      name: `${wpName} - ${dateTime}`,
      description: '',
      businessUnit: WORK_PACKAGE_BUSINESS_UNIT.DAIRY,
      type: WORK_PACKAGE_TYPE.ASSORTMENT_FULL_RESET,
      targetLaunchDate: utcStartOfDay.format(saveDateFormat),
      isTemplate: !!isTemplate,
      withoutBundle,
      status: isTemplate ? merge({}, baseStatus.workpackageBaseStatusObject) : {},
      useMarkdownAndWasteToCalculateMarginAndPrice: false,
    };
  }

  async function fetchSnapshotDates() {
    const [err, res] = await to(axios.get('/api/snapshot-dates'));
    if (err) throw new Error(err.message);
    // Set the latest date to be the last actual date, not the future one
    const actualDates = filter(res.data, sd => !sd.isFuture);

    latestSnapshotDate.value = actualDates[actualDates.length - 1].snapshotDate;
    snapshotDates.value = res.data.map(d => d.snapshotDate);
  }

  async function addWorkpackage({ isTemplate, templateId, bundleId, withoutBundle }) {
    loading.value = true;

    try {
      if (!latestSnapshotDate.value) {
        await fetchSnapshotDates();
      }

      const wpBody = {
        ...createWorkpackage(isTemplate, withoutBundle),
        templateId,
        bundleId,
      };
      const [err, result] = await to(axios.post('/api/workpackages/', wpBody));
      if (err || (result.data && result.data.errors)) {
        throw new Error(`Unable to create workpackage: ${result?.data?.name}`);
      }
      const { insertedWorkpackage } = result.data;
      if (insertedWorkpackage) addWorkpackageMutation(insertedWorkpackage);
      return insertedWorkpackage;
    } finally {
      loading.value = false;
    }
  }

  async function refreshWorkpackageInformation({ workpackageId, forceStateUpdate = false }) {
    const { data } = await axios.get(`/api/workpackages/${workpackageId}`);
    setWorkpackageById(data);

    if (workpackageId === get(selectedWorkpackage.value, '_id') || forceStateUpdate) {
      setSelectedWorkpackage(data);
    }
  }

  async function updateWorkpackage({ workpackageId, updates, omitStoreUpdate }) {
    const omitUpdate = omitStoreUpdate || false;
    loading.value = true;
    if (updates._id && updates._id !== workpackageId) {
      throw new Error('mismatched workpackageId on update');
    }
    const [err, result] = await to(axios.patch(`/api/workpackages/${workpackageId}`, updates));
    loading.value = false;
    if (err) {
      const message = get(err, ['response', 'data', 'message'], err.message);
      throw new Error(message);
    }

    if (result && !omitUpdate) {
      const { lastModifiedDate, storeCount, productCount } = result.data.updates;
      // AOV3-610 TODO: commit 1 mutation here
      forEach(
        { ...omit(updates, ['_id']), lastModifiedDate, storeCount, productCount },
        (value, key) => {
          updateWorkpackageMutation({ workpackageId, field: key, updates: value });
        }
      );
    }
    if (workpackageId === get(selectedWorkpackage.value, '_id')) {
      await refreshWorkpackageInformation({ workpackageId });
    }
  }

  async function fetchArchivedWorkpackages(filterParams = {}) {
    const { limit, offset, regexSearch } = filterParams;
    loadingArchivedWorkpackages.value = true;
    const pickedFields = [...workpackageDefaultFields, ...['archived', 'archiveDate']];
    // Return archived workpackage templates if requested
    const shouldGetTemplates = get(filterParams, 'shouldGetTemplates', false);

    const query = {
      archived: true,
      isTemplate: shouldGetTemplates ? true : { $ne: true },
    };

    let params = {
      sortField: 'creationDate',
      sortDirection: SORT_DIRECTION.descending,
      pick: pickedFields,
      where: query,
      regexSearch,
    };
    if (!isNaN(limit) && !isNaN(offset)) {
      params = { ...params, ...{ limit, offset } };
    }
    const [err, result] = await to(axios.get('/api/workpackages', { params }));
    loadingArchivedWorkpackages.value = false;
    if (err) throw new Error(err.message);
    setArchivedWorkpackagesMutation({
      workpackages: result.data.workpackages,
      totalCount: result.data.totalCount,
    });
  }

  async function fetchWorkpackages(customParams = {}) {
    const shouldShowSpinner = get(customParams, 'shouldShowSpinner', true);

    if (shouldShowSpinner) loading.value = true;

    let sortedWorkpackages = [];

    // Update store state by default, but allow to choose
    const shouldUpdateState = get(customParams, 'shouldUpdateState', true);

    try {
      assortmentCanvasStore.fetchFinalWorkpackages();
      const pickedFields = workpackageDefaultFields.concat(
        get(customParams, 'pickExtra', ['bundleId'])
      );
      // Return workpackage templates if requested
      const shouldGetTemplates = get(customParams, 'shouldGetTemplates', false);

      // Return final workpackages only if requested
      const shouldGetFinalOnly = get(customParams, 'shouldGetFinalOnly', false);

      // Only fetch workpackages for bundle if bundleId param is provided
      const bundleId = get(customParams, 'bundleId');

      const query = {
        isTemplate: shouldGetTemplates ? true : { $ne: true },
        ...(shouldGetFinalOnly && {
          _id: { $in: [...assortmentCanvasStore.finalWorkpackageIds] },
        }),
        ...(bundleId && { bundleId }),
        ...(customParams.filter && customParams.filter),
      };

      // Allow to completely ignore archived state of the workpackage,
      // but fetch within non-archived (active) by default
      const ignoreArchivedFlag = get(customParams, 'ignoreArchivedFlag');
      if (!ignoreArchivedFlag) {
        query.archived = false;
      }

      const params = {
        sortField: get(customParams, 'sortField', 'creationDate'),
        sortDirection: get(customParams, 'sortDirection', SORT_DIRECTION.ascending),
        pick: customParams.pick || pickedFields,
        where: query,
        // Add pagination if provided
        ...(!isNaN(customParams.limit) && { limit: customParams.limit }),
        ...(!isNaN(customParams.offset) && { offset: customParams.offset }),
        ...(customParams.regexSearch && { regexSearch: customParams.regexSearch }),
      };

      const [, result] = await to(axios.get('/api/workpackages', { params }));

      sortedWorkpackages = result.data.workpackages.sort((a, b) =>
        a.creationDate.localeCompare(b.creationDate)
      );

      if (shouldUpdateState) {
        if (shouldGetTemplates) {
          workpackageTemplates.value = sortedWorkpackages;
        } else {
          workpackages.value = sortedWorkpackages;
        }

        // when this action is called from template's section, we also update the currently select wp
        if (shouldGetTemplates && selectedWorkpackage.value) {
          refreshWorkpackageInformation({ workpackageId: selectedWorkpackage.value._id });
        }
      }

      return sortedWorkpackages;
    } catch (err) {
      throw new Error(err.message);
    } finally {
      loading.value = false;
    }
  }

  function toggleScenarioPanelExpanded({ workpackage } = { workpackage: undefined }) {
    // Collapse workpackage scope in case it's expanded
    isWPSetupExpanded.value = false;

    if (
      !workpackage ||
      (isScenarioPanelExpanded.value && get(selectedWorkpackage.value, '_id') === workpackage._id)
    ) {
      scenariosStore.scenarios = [];
      setSelectedWorkpackage(null);
      isScenarioPanelExpanded.value = false;
    } else {
      scenariosStore.scenarios = [];
      setSelectedWorkpackage(workpackage);
      isScenarioPanelExpanded.value = true;
    }
  }

  function toggleExpanded(workpackage) {
    const value = expanded.value === workpackage ? null : workpackage;
    expanded.value = value;
  }

  async function processCSV({ fileId, mappings, delimiter, service }) {
    const workpackage = cloneDeep(selectedWorkpackage.value);
    loading.value = true;
    const [err, res] = await to(
      axios.post(`/api/csv/${service}/process/${fileId}`, {
        mappings,
        delimiter,
      })
    );
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);

      return;
    }
    const { messages, tableRows } = res?.data ?? {};
    if (size(tableRows)) workpackage.stores = tableRows;

    setSelectedWorkpackage(workpackage);
    if (size(messages)) {
      alertsStore.showMessages(messages);
    }

    return tableRows;
  }

  /*
   * This method partially updates selected workpackage with job status acquired from the events stream
   */
  async function updateWorkpackageJobStatus({ message } = { message: undefined }) {
    if (!selectedWorkpackage.value) return;
    const workpackageId = get(message, 'workpackageId', selectedWorkpackage.value._id);
    const pickFields = ['_id', 'jobs'];
    const [err, res] = await to(
      axios.get(`/api/workpackages/${workpackageId}`, { params: { pick: pickFields } })
    );
    if (err) throw new Error(err.message);

    if (res.data.jobs) {
      // Create a new object for jobs to ensure reactivity
      const newJobs = { ...res.data.jobs };

      // Update only the jobs prop to avoid reactivity issues
      const workpackageIndex = workpackages.value.findIndex(wp => wp._id === workpackageId);
      if (workpackageIndex !== -1) {
        workpackages.value[workpackageIndex].jobs = newJobs;
      }

      // If it's a template, update the template array.
      // This is needed for the template list, even though the same WP is in the workpackages array
      const templateIndex = workpackageTemplates.value.findIndex(wp => wp._id === workpackageId);
      if (templateIndex !== -1) {
        workpackageTemplates.value[templateIndex].jobs = newJobs;
      }
    }
  }

  async function runWorkpackageSetupJob() {
    const workpackageId = selectedWorkpackage.value._id;
    loading.value = true;
    const [err, response] = await to(axios.post(`/api/workpackages/${workpackageId}/run-setup`));
    loading.value = false;
    updateWorkpackageJobStatus();
    if (err) {
      snackbarStore.showError(i18n.t('errors.generalServerError'));
      throw new Error(err.message);
    }
    handleErrorMessages({ response, options: { errorSuffix: 'invalidStores' } });
  }

  function setIsImportingFromPlano(isImporting) {
    isImportingFromPlano.value = isImporting;
  }

  function setLastSuccessfulImportFromPlanoTimestamp(timestamp) {
    lastSuccessfulImportFromPlanoTimestamp.value = timestamp;
  }

  function capWeeks(weeks, date, maxDate) {
    const availableWeeks = floor(maxDate.diff(date, 'day') / 7);
    weeks = max([weeks, 1]);
    weeks = min([weeks, availableWeeks]);

    return weeks;
  }

  function updateEndDatesExceeded(type) {
    endDatesExceeded.value[type] = true;
    // This powers the tooltips on workpackage/ template. While true, tooltip is displayed.
    // The timeout is to unset this, and hide the tooltip, after X seconds.
    setTimeout(() => {
      endDatesExceeded.value[type] = false;
    }, 3000);
  }

  function changeStartOrEndDate({ date, weeks, type }) {
    const { dayjs } = useDayjs();
    if (!useEndDateSelection.value || isApplyingPreSet.value) {
      const maxDate = getMaxDate();

      const adjustedWeeks = capWeeks(weeks, date, maxDate);
      if (weeks !== adjustedWeeks) updateEndDatesExceeded(type);

      const res = dayjs.utc(date).add(adjustedWeeks, 'weeks').subtract(1, 'day').format(dateFormat);

      return {
        [`${type}EndDate`]: res,
        [`${type}DateWeeks`]: adjustedWeeks,
      };
    }

    const res = dayjs.utc(date).subtract(weeks, 'weeks').add(1, 'day').format(dateFormat);

    return { [`${type}StartDate`]: res };
  }

  function changeSnapshotDate(snapshotDate) {
    const { goToDay } = useDayjs();
    const { hasSeasonalityEnabled } = useFeature();

    // If snapshot date is changed, previous AG selections are removed
    wpSelectedAssortmentGroups.value = [];

    const maxDate = getMaxDate(snapshotDate);
    const lastAvailableSunday = goToDay(maxDate, 'prev', 'sunday');

    return {
      ...changeEndDate(lastAvailableSunday, 'switching'),
      // when feature is off, these fields are not filled/visible so cannot be changed
      ...(hasSeasonalityEnabled.value ? changeEndDate(lastAvailableSunday, 'learning') : {}),
      ...changeEndDate(lastAvailableSunday, 'performance'),
    };
  }

  function updateWpPreSetSelections(preSet) {
    // Calculate the start and end weeks based on the selected option
    let { startWeek, endWeek } = preSet;

    // Helper function to get the monday after offsetting by a number of weeks
    const getMondayOfOffsetWeek = (startDate, week) => {
      // Create a date object for the first day of the year and offset
      let date = startDate.startOf('year').add(week, 'weeks');
      // Adjust the date to the first Monday of the week
      date = date.subtract(date.day() === 0 ? 6 : date.day() - 1, 'days');
      return date;
    };

    const { toNearestSunday } = useDayjs();
    const latestSelectableDate = toNearestSunday(this.latestSnapshotDate);

    let startDate = getMondayOfOffsetWeek(latestSelectableDate, startWeek);
    let endDate = getMondayOfOffsetWeek(latestSelectableDate, endWeek);

    // If the end date is after the current date, it should go back an extra year, ensuring that it is still picking the week of the year
    if (endDate.isAfter(latestSelectableDate)) {
      startWeek -= 52;
      endWeek -= 52;

      startDate = getMondayOfOffsetWeek(latestSelectableDate, startWeek);
      endDate = getMondayOfOffsetWeek(latestSelectableDate, endWeek);
    }

    this.isApplyingPreSet = true;
    this.updateWpDateSelections({ field: 'performancePeriodPreSet', value: preSet.id });

    // Update the Workpackage date selections with the new dates
    this.updateWpDateSelections({ field: 'performanceStartDate', value: startDate });
    const weeks = endDate.diff(startDate, 'weeks') + 1;
    this.updateWpDateSelections({ field: 'performanceDateWeeks', value: weeks });

    this.isApplyingPreSet = false;
  }

  function updateWpDateSelections(updates) {
    if (size(updates) > 2) throw new Error('nope');

    const wp = { ...wpDateSelections.value };
    const { field, value } = updates;

    // If changing any dates manually, need to reset the pre-set selection
    const checkStatuses = [
      'performanceStartDate',
      'performanceEndDate',
      'performanceDateWeeks',
      'performanceExcludedDates',
    ];
    // Ensure no circular loops by checking we're not currently applying a preset
    if (!this.isApplyingPreSet && checkStatuses.includes(field)) {
      this.updateWpDateSelections({ field: 'performancePeriodPreSet', value: null });
    }

    const triggers = {
      // Need to take the end date for week selection changes in case we are using end date selection,
      // except when we are using a preset (which picks from the start date)
      performanceStartDate: { date: value, weeks: wp.performanceDateWeeks, type: 'performance' },
      performanceEndDate: { date: value, weeks: wp.performanceDateWeeks, type: 'performance' },
      performanceDateWeeks: {
        date:
          useEndDateSelection.value && !isApplyingPreSet.value
            ? wp.performanceEndDate
            : wp.performanceStartDate,
        weeks: value,
        type: 'performance',
      },

      learningStartDate: { date: value, weeks: wp.learningDateWeeks, type: 'learning' },
      learningEndDate: { date: value, weeks: wp.learningDateWeeks, type: 'learning' },
      learningDateWeeks: {
        date:
          useEndDateSelection.value && !isApplyingPreSet.value
            ? wp.learningEndDate
            : wp.learningStartDate,
        weeks: value,
        type: 'learning',
      },

      switchingStartDate: { date: value, weeks: wp.switchingDateWeeks, type: 'switching' },
      switchingEndDate: { date: value, weeks: wp.switchingDateWeeks, type: 'switching' },
      switchingDateWeeks: {
        date:
          useEndDateSelection.value && !isApplyingPreSet.value
            ? wp.switchingEndDate
            : wp.switchingStartDate,
        weeks: value,
        type: 'switching',
      },
    };

    const triggeredField = triggers[field];
    let changedDates = triggeredField ? changeStartOrEndDate(triggeredField) : {};

    if (field === 'snapshotDate') changedDates = changeSnapshotDate(value);

    // Get the updated value and apply it
    const newValues = {
      ...wpDateSelections.value,
      ...{ [field]: value },
      ...changedDates,
    };

    wpDateSelections.value = formatDates({ dates: newValues });

    // Whenever this changes, we need to refetch
    if (field === 'snapshotDate') assortmentGroupsStore.fetchAssortmentGroups();
  }

  function updateSelectedAssortmentGroups(updates) {
    const onlySavedFields = mapValues(updates, x => pick(x, ['key', 'assortment', 'analysis']));
    wpSelectedAssortmentGroups.value = sortBy(onlySavedFields, 'key');
  }

  function updateWpModelSelections(updates) {
    const newValues = {
      ...wpModelSelections.value,
      ...updates,
    };
    wpModelSelections.value = newValues;
  }

  function setScenarioPanelExpanded(value) {
    isScenarioPanelExpanded.value = value;
  }

  function setLoadingWorkpackageTemplates(isLoading) {
    loadingWorkpackageTemplates.value = isLoading;
  }

  function addProductsHierarchy(products) {
    productsHierarchy.value = products;
  }

  async function copyWorkpackage(workpackage) {
    loading.value = true;
    setSelectedWorkpackage(workpackage);
    const [err, response] = await to(axios.post(`/api/workpackages/copy/${workpackage._id}`));
    loading.value = false;
    if (err) throw new Error(err.message);
    updateWorkpackageJobStatus();
    return response;
  }

  function setWorkpackageSetupExpanded(value) {
    isWPSetupExpanded.value = value;
  }

  function setWorkpackages(newWorkpackages) {
    workpackages.value = newWorkpackages;
  }

  function deleteWorkpackageById(wpId) {
    const localWorkpackages = [];
    workpackages.value.forEach(wp => {
      if (wp._id === wpId) return;
      localWorkpackages.push(wp);
    });
    workpackages.value = localWorkpackages;
  }

  function deleteWorkpackageTemplateById(wpId) {
    const filteredWorkpackageTemplates = [];
    workpackageTemplates.value.forEach(wp => {
      if (wp._id === wpId) return;
      filteredWorkpackageTemplates.push(wp);
    });
    workpackageTemplates.value = filteredWorkpackageTemplates;
  }

  async function toggleWorkpackageSetupExpanded({ workpackage }) {
    // Collapse scenario panel in case it's expanded
    setScenarioPanelExpanded(false);

    if (
      !workpackage ||
      ((selectedWorkpackage.value || {})._id === workpackage._id && isWPSetupExpanded.value)
    ) {
      setSelectedWorkpackage(null);
      setWorkpackageSetupExpanded(false);
    } else {
      // Clear stale setup data from the previously opened WP scope to prevent
      // AssortmentGroupsTab from mounting prematurely with wrong wpDateSelections.
      wpDateSelections.value = {};
      wpModelSelections.value = {};
      wpSelectedAssortmentGroups.value = [];
      const { data } = await axios.get(`/api/workpackages/${workpackage._id}`);
      setSelectedWorkpackage(data);
      setWorkpackageSetupExpanded(true);
    }
  }

  async function deleteWorkpackage(workpackage) {
    if (!workpackage._id) {
      workpackages.value.pop();
      return;
    }
    loading.value = true;
    const [err, result] = await to(axios.delete(`/api/workpackages/${workpackage._id}`));
    loading.value = false;
    if (err) throw new Error(err.message);
    if (result.data && result.data.errors) {
      throw new Error(`Unable to delete workpackage: ${workpackage.name}`);
    }
    const bundleId = get(bundlesStore.selectedBundle, '_id');
    fetchWorkpackages({ bundleId, shouldGetTemplates: workpackage.isTemplate });
  }

  async function deleteTemplate(template) {
    loading.value = true;
    const [err, result] = await to(axios.delete(`/api/workpackages/${template._id}/template`));
    loading.value = false;
    if (err) throw new Error(err.message);
    if (result.data && result.data.errors) {
      throw new Error(`Unable to delete template: ${template.name}`);
    }
    fetchWorkpackages({ shouldGetTemplates: template.isTemplate, pickExtra: ['status'] });
  }

  async function afterTemplateSetupComplete(newScenarioId) {
    scenariosStore.loadScenario(newScenarioId);
  }

  // Checks the statuses of jobs given a list of workpackage ids. Updating the state for any that have completed
  async function afterWorkpackageSetupComplete({ message }) {
    const { workpackageId } = message;
    const [err, res] = await to(axios.get(`/api/workpackages/${workpackageId}`));
    if (err) throw new Error(err.message);

    const { name: wpName, jobs, isTemplate } = res.data;
    updateWorkpackageMutation({ workpackageId, field: 'jobs', updates: jobs });
    const bundleId = get(bundlesStore.bundles.selectedBundle, '_id');
    fetchWorkpackages({ bundleId, shouldGetTemplates: isTemplate });

    const translationKey = isTemplate ? 'templateSetup' : 'workpackageSetup';
    const content = i18n.t(`notifications.${translationKey}.finished`, { workpackage: wpName });
    const color = 'success';
    snackbarStore.showSnackbar({ content, color });

    if (isTemplate) {
      const { taskOutputs } = message;
      const templateSetupResult = taskOutputs?.find(
        task => task.taskName === jobapi.jobTypes.templateSetup
      );

      if (!templateSetupResult.success) {
        snackbarStore.showError(i18n.t('errors.templateScenarioCreationFailure'));
        return;
      }
      afterTemplateSetupComplete(templateSetupResult.outputs.newScenario._id);
    }
  }

  async function afterWorkpackageSetupFailed({ message }) {
    const [err, res] = await to(axios.get(`/api/workpackages/${message.workpackageId}`));
    if (err) throw new Error(err.message);

    const wp = res.data;
    updateWorkpackageMutation({ workpackageId: wp._id, field: 'jobs', updates: wp.jobs });
    const bundleId = get(bundlesStore.bundles.selectedBundle, '_id');
    fetchWorkpackages({ bundleId, shouldGetTemplates: wp.isTemplate });

    const translationKey = wp.isTemplate ? 'templateSetup' : 'workpackageSetup';
    const content = i18n.t(`notifications.${translationKey}.failed`, { workpackage: wp.name });
    const color = 'error';
    snackbarStore.showSnackbar({ content, color });
  }

  async function afterCopyWorkpackageComplete({ message }) {
    const [err, res] = await to(axios.get(`/api/workpackages/${message.workpackageId}`));
    if (err) throw new Error(err.message);

    const wp = res.data;
    updateWorkpackageMutation({ workpackageId: wp._id, field: 'jobs', updates: wp.jobs });
    const bundleId = get(bundlesStore.bundles.selectedBundle, '_id');
    fetchWorkpackages({ bundleId, shouldGetTemplates: wp.isTemplate });

    const content = i18n.t('notifications.copyWorkpackage.finished', { name: wp.name });
    const color = 'success';
    snackbarStore.showSnackbar({ content, color });
  }

  async function afterCopyWorkpackageFailed({ message }) {
    const [err, res] = await to(axios.get(`/api/workpackages/${message.workpackageId}`));
    if (err) throw new Error(err.message);

    const wp = res.data;
    updateWorkpackageMutation({ workpackageId: wp._id, field: 'jobs', updates: wp.jobs });
    const bundleId = get(bundlesStore.bundles.selectedBundle, '_id');
    fetchWorkpackages({ bundleId, shouldGetTemplates: wp.isTemplate });

    const content = i18n.t('notifications.copyWorkpackage.failed', { workpackage: wp.name });
    const color = 'error';
    snackbarStore.showSnackbar({ content, color });
  }

  async function importAGsForPeriodId({ periodIds }) {
    loadingAGsForPeriodId.value = true;

    const [err, assortmentGroups] = await to(
      jobApi.runFunction('run-import-assortment-groups', { period_ids: periodIds })
    );
    loadingAGsForPeriodId.value = false;
    if (err) throw new Error(err.message);
    return assortmentGroups;
  }

  async function handleAutoAssortmentGroups() {
    const { dayjs } = useDayjs();
    const { snapshotDate } = wpDateSelections.value;
    const { selectedChain, periodIds } = wpModelSelections.value;

    // Ensure masterAssortmentGroups is loaded for the current snapshot before setting proposals.
    // This prevents a race condition where proposals are set before the AG list is available.
    await assortmentGroupsStore.fetchAssortmentGroups(snapshotDate);

    let proposedAGs = await importAGsForPeriodId({ periodIds });

    proposedAGs = proposedAGs.map(ag => {
      ag.key = `${dayjs(snapshotDate)
        .format('YYYY-MM-DD')
        .replaceAll('-', '_')}_${ag.assortmentGroup}`;
      return ag;
    });

    proposedAssortmentGroups.value = {
      // Chain is only set for selections coming from workpackage setup
      selectedChain,
      proposedAGs,
    };
  }

  function calculateDatesFromPeriodId(startDate, endDate) {
    const { dayjs, goToDay } = useDayjs();
    startDate = dayjs(startDate);
    endDate = dayjs(endDate);

    const snapshotDate = goToDay(startDate, 'next', 'monday');
    const latestDataDate = getLatestDataDate();

    let performanceStartDate = startDate.subtract(1, 'year');

    // Ensure the performance start date is right.
    const thursday = 4; // Should be nearest monday - go back if thursday or earlier, forward otherwise.
    const direction = performanceStartDate.isoWeekday() <= thursday ? 'previous' : 'next';
    performanceStartDate = goToDay(performanceStartDate, direction, 'monday');

    // We want to match the period's length if possible - but we can't exceed the data available.
    const originalPeriodLength = floor(endDate.diff(startDate, 'day') / 7) + 1;
    const idealPerformanceEndDate = performanceStartDate.add(originalPeriodLength, 'weeks');
    const boundedPerformanceEndDate = dayjs.min(latestDataDate, idealPerformanceEndDate);
    const adjustedBoundedPerformanceEndDate = goToDay(
      boundedPerformanceEndDate,
      'previous',
      'sunday'
    );

    const diffInWeeks = adjustedBoundedPerformanceEndDate.diff(performanceStartDate, 'day') / 7;
    const performanceDateWeeks = Math.round(diffInWeeks);

    const changes = {
      snapshotDate,
      targetLaunchDate: startDate,
      performanceStartDate,
      performanceDateWeeks,
      performanceEndDate: performanceStartDate
        .add(performanceDateWeeks, 'weeks')
        .subtract(1, 'day'),
    };

    wpDateSelections.value = formatDates({
      dates: {
        ...wpDateSelections.value,
        ...changes,
      },
    });

    // The snapshot date has changed - you must re-fetch assortment groups
    assortmentGroupsStore.fetchAssortmentGroups();
  }

  function getDatesForSave() {
    return formatDates({ dates: wpDateSelections.value, format: saveDateFormat });
  }

  function initWpSetupData() {
    const savedWp = selectedWorkpackage.value;

    // Setup dates
    // Not strictly necessary - only required for backwards compat with workpackages that were never used
    const defaultDates = getDefaultDates();
    const wpDates = savedWp ? pick(selectedWorkpackage.value, keys(defaultDates)) : {};
    const dateUpdates = { ...defaultDates, ...wpDates };

    // Assign directly - the update logic assumes you are changing existing data and does date math.
    wpDateSelections.value = formatDates({ dates: dateUpdates, parseFromDb: true });
    updateSelectedAssortmentGroups(savedWp.selectedAssortmentGroupSettings);
  }

  function getWpDataForSave() {
    const { hasSeasonalityEnabled } = useFeature();
    // All data is already formatted. We do it whenever it's set, in updateWpDateSelections
    const dataForSave = {
      ...savedWorkpackage.value,
      ...getDatesForSave(),
      ...wpModelSelections.value,
      selectedAssortmentGroupSettings: wpSelectedAssortmentGroups.value.map(ag => {
        return pick(ag, ['key', 'analysis', 'assortment']);
      }),
      stores: selectedWorkpackage.value.stores,
    };

    const keysToOmit = [
      'customLearningDateWeeks',
      'customPerformanceDateWeeks',
      'customSwitchingDateWeeks',
      'assortmentGroups',
      'totalNotes',
    ];

    if (!hasSeasonalityEnabled.value) {
      keysToOmit.push(
        ...['learningStartDate', 'learningEndDate', 'learningDateWeeks', 'learningExcludedDates']
      );
    }
    return omit(dataForSave, keysToOmit);
  }

  function resetWpSetupChanges() {
    // SelectedWorkpackage is our saved version. Reset to that.
    const dateSelections = keys(wpDateSelections.value);
    const modelSelections = keys(wpModelSelections.value);

    const savedWpValues = { ...selectedWorkpackage.value };

    wpDateSelections.value = pick(savedWpValues, dateSelections);
    updateWpModelSelections(pick(savedWpValues, modelSelections));
    updateSelectedAssortmentGroups(savedWpValues.selectedAssortmentGroupSettings);
    // Needs to reset selectedAssortmentGroups
  }

  function clearWpSetupChanges() {
    wpDateSelections.value = {};
    wpModelSelections.value = {};
    wpSelectedAssortmentGroups.value = [];
  }

  return {
    // state
    selectedWorkpackage,
    selectedWpLinkedWp,
    selectedWpLinkedScenario,
    savedWorkpackage,
    workpackages,
    archivedWorkpackages,
    additionalDownloadedWorkpackages,
    workpackageTemplates,
    archivedWorkpackageTemplates,
    archivedWorkpackagesTotalCount,
    archivedWorkpackageTemplatesTotalCount,
    archivedWorkpackagesCurrentPage,
    archivedWorkpackageTemplatesCurrentPage,
    isScenarioPanelExpanded,
    isWPSetupExpanded,
    loading,
    loadingArchivedWorkpackages,
    loadingWorkpackageTemplates,
    wpValidation,
    productsHierarchy,
    hierarchyDepth,
    isImportingFromPlano,
    lastSuccessfulImportFromPlanoTimestamp,
    loadingAGsForPeriodId,
    useEndDateSelection,
    wpDateSelections,
    wpModelSelections,
    wpSelectedAssortmentGroups,
    latestSnapshotDate,
    snapshotDates,
    endDatesExceeded,

    // getters
    activeWorkpackages,
    getWorkpackageById,
    workpackagesById,
    workpackagesByBundle,
    getUnitOfMeasure,
    stores,
    formattedSnapshotDates,

    isFullResetWP,
    isSimpleSwapsWP,
    isFreshWP,
    isApplyingPreSet,
    isCopyWorkpackageRunning,
    getSelectedWorkpackageExcludedDates,
    workpackageStatus,
    selectedWorkpackageStatus,
    isWorkpackageSetupRunning,
    templatesById,
    proposedAssortmentGroups,
    getPreviousChainedWorkpackage,
    // mutations - TODO remove
    updateNoteCountMutation,
    // actions
    setWorkpackageById,
    tddWorkpackageHelper,
    loadState,
    getProductHierarchy,
    getProductHierarchyDepth,
    getProductsHierarchyProductsData,
    addWorkpackage,
    updateWorkpackage,
    setSelectedWorkpackage,
    refreshWorkpackageInformation,

    fetchSingleWorkpackage,
    fetchWorkpackages,
    fetchArchivedWorkpackages,
    fetchSnapshotDates,

    toggleScenarioPanelExpanded,
    toggleExpanded,
    processCSV,
    updateWorkpackageJobStatus,
    runWorkpackageSetupJob,
    setIsImportingFromPlano,
    setLastSuccessfulImportFromPlanoTimestamp,
    updateWpPreSetSelections,
    updateWpDateSelections,
    updateWpModelSelections,
    setScenarioPanelExpanded,
    setLoadingWorkpackageTemplates,
    addProductsHierarchy,
    setWorkpackageSetupExpanded,
    setWorkpackages,
    deleteWorkpackageById,
    deleteWorkpackageTemplateById,
    toggleWorkpackageSetupExpanded,
    copyWorkpackage,
    deleteWorkpackage,
    deleteTemplate,
    afterWorkpackageSetupComplete,
    afterWorkpackageSetupFailed,
    afterTemplateSetupComplete,
    afterCopyWorkpackageComplete,
    afterCopyWorkpackageFailed,
    importAGsForPeriodId,
    handleAutoAssortmentGroups,
    calculateDatesFromPeriodId,
    initWpSetupData,
    updateSelectedAssortmentGroups,
    getDatesForSave,
    getWpDataForSave,
    resetWpSetupChanges,
    clearWpSetupChanges,
    getDefaultDates,
    formatDates,
  };
});
